import {right} from "@popperjs/core";
import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {DomainApi, type DomainValue, SortDirection, UserLocality} from "components/src/api/admin/generated";
import {ActionsDropdown} from "components/src/common/ActionsDropdown";
import {ClearSelection} from "components/src/common/ClearSelection";
import {ConfirmDialog} from "components/src/common/ConfirmDialog";
import {InvertSelection} from "components/src/common/InvertSelection";
import {Pager} from "components/src/common/Pager";
import {RenderAlert} from "components/src/common/RenderAlert";
import {RowsPerPage} from "components/src/common/RowsPerPage";
import {SortIcon} from "components/src/common/SortIcon";
import {Truncate} from "components/src/common/Truncate";
import {useHasAllPermissions} from "components/src/hook/UseHasAllPermissions";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {PageAlert} from "components/src/layout/PageAlert.tsx";
import {PageContent} from "components/src/layout/PageContent.tsx";
import {PageFooter} from "components/src/layout/PageFooter.tsx";
import {PageHeader} from "components/src/layout/PageHeader.tsx";
import {SubLevelSettingsPopup} from "components/src/settings/SubLevelSettingsPopup";
import {logger} from "components/src/utils/Logger";
import {Utils} from "components/src/utils/Utils";
import {Bin as DeleteIcon, Dns as DomainsIcon, Home as InternalDomainIcon, Plus as AddDomainIcon} from "iconoir-react";
import {useEffect, useRef, useState} from 'react'
import {Dropdown} from "react-bootstrap";
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Modal from 'react-bootstrap/Modal';
import Nav from "react-bootstrap/Nav";
import Navbar from 'react-bootstrap/Navbar';
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import Tooltip from "react-bootstrap/Tooltip";
import {useLocation, useNavigate} from "react-router-dom";

/*
 * REST api for accessing the back-end
 */
const domainApi = new DomainApi(ApiConfig.config());

function getLinkTarget(domain: string): string {
    return "/domains/settings/general/" + encodeURIComponent(domain);
}

/**
 * Users component.
 *
 * Provides functionality to manage domains, including pagination, adding and deleting domains.
 * Renders a table of domains with options to select and perform actions on domains.
 *
 * @returns {ReactElement} The Domains component.
 */
export const Domains = () => {
    /**
     * The path segments have the following structure
     *
     * path :== /domain/<category>/<domain> | /user/<category>/<email>
     *
     * <category> :== <category-name> | <category-name>/<category>
     * <category-name> :== <letter> | <letter><category-name>
     */
    const location = useLocation()
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated or to some other page
     * if the dialog is closed
     */
    const navigate = useNavigate();
    /**
     * The loaded domains
     */
    const [domains, setDomains] = useState<DomainValue[]>([]);
    /**
     * The total number of domains
     */
    const [domainCount, setDomainCount] = useState<number>(0);
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The index of the first page to show
     */
    const [currentPage, setCurrentPage] = useState<number>(1);
    /**
     * The max number of domains to show
     */
    const [rowsPerPage, setRowsPerPage] = useState<number>(40);
    /**
     * The sort direction of the domains
     */
    const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.Asc);
    /**
     * The selected domains
     */
    const [selectedDomains, setSelectedDomains] = useState<Array<string>>([]);
    /**
     * If true, the "add domain" modal dialog will be shown
     */
    const [showAddDomain, setShowAddDomain] = useState<boolean>(false);
    /**
     * If true, the "confirm deletion" modal dialog will be shown
     */
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    /**
     * If true, the domains will be reloaded from the back-end
     */
    const [forceReload, setForceReload] = useState<boolean>(false);
    /**
     * The error message if a validation error occurred
     */
    const [validationErrorMessage, setValidationErrorMessage] = useState<string>();
    /**
     * Reference to the domain field from the add domain modal. This is used to get the domain.
     */
    const domainControlRef = useRef<HTMLInputElement>(null);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /*
     * A helper for checking whether all required permissions are granted.
     */
    const hasAllPermissions = useHasAllPermissions();
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    useEffect(() => {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // reset state when we load new data
        setForceReload(false);

        domainApi.domainControllerGetDomainCount()
            .then(response => {
                setDomainCount(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting domain count", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [navigate,
        forceReload,
        setSpinnerStateCounter,
        t
    ]);

    useEffect(() =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // reset state when we load new data
        setErrorMessage(undefined);
        setSelectedDomains([]);
        setShowAddDomain(false);
        setShowDeleteConfirm(false);
        setForceReload(false);
        setValidationErrorMessage(undefined);

        domainApi.domainControllerGetDomains((currentPage - 1) * rowsPerPage, rowsPerPage, sortDirection)
            .then(response => {
                setDomains(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting domains", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count - 1);
        }
    }, [navigate,
        forceReload,
        setSpinnerStateCounter,
        sortDirection,
        currentPage,
        rowsPerPage,
        t
    ]);

    const onChangeSelect = (checked : boolean, domain : string) =>
    {
        setSelectedDomains(checked ?
            [...selectedDomains, domain] :
            selectedDomains.filter(e => e !== domain));
    }

    const addDomain = () =>
    {
        const domain = domainControlRef.current?.value;

        if (domain === undefined || domain.trim().length === 0)
        {
            setValidationErrorMessage("Domain is required")

            return;
        }

        domainApi.domainControllerAddDomain(domain)
            .then(() => {
                setForceReload(true);
                // navigate to settings for the domain
                setShowAddDomain(false);
                navigate(getLinkTarget(domain));
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error adding domain", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setValidationErrorMessage(Utils.getErrorMessage(err, t));
            })
    }

    const showAddDomainModal = () =>
    {
        // reset error message
        setValidationErrorMessage(undefined);
        setShowAddDomain(true)
    }

    const deleteSelected = () =>
    {
        domainApi.domainControllerDeleteDomains(selectedDomains)
            .then(() => {
                setForceReload(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error deleting domains", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
            setShowDeleteConfirm(false);
            setSelectedDomains([]);
        })
    }

    const invertSelection = () => {
        // domains which are in use should never be selected
        setSelectedDomains([...domains].filter(d => !selectedDomains.includes(d.domain))
            .filter(d => !d.inUse).map(d => d.domain))
    }

    return (
        <>
            <Modal show={showAddDomain}
                   onHide={() => setShowAddDomain(false)}
                   onEntered={() => domainControlRef.current?.focus()}
                   backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>{t("domains.add-domain")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <RenderAlert message={validationErrorMessage} setMessage={setValidationErrorMessage}/>
                    <InputGroup className="mb-3">
                        <Form.Control
                            type="text"
                            placeholder="Domain"
                            ref={domainControlRef}
                            isInvalid={validationErrorMessage !== undefined}/>
                    </InputGroup>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="success" onClick={() => addDomain()}>
                        {t("domains.add")}
                    </Button>
                </Modal.Footer>
            </Modal>

            <ConfirmDialog title={t("common.confirm")}
                           bodyText={t("domains.are-you-sure-delete-domains")}
                           confirmButtonTitle={t("common.delete")}
                           onClickConfirmEvent={deleteSelected}
                           showConfirm={showDeleteConfirm}
                           setShowConfirm={setShowDeleteConfirm}
            />

            {/^\/domains\/.+/i.test(location.pathname) && <SubLevelSettingsPopup />}

            <div className="d-flex flex-column h-100">
                <PageHeader icon={<DomainsIcon />} title={t("domains.title")} onClickReload={() => setForceReload(true)}/>

                <PageAlert message={errorMessage} setMessage={setErrorMessage}/>

                <div className="px-4">
                    <Row>
                        <div className="d-flex flex-row align-items-center mb-4">
                            <div className="d-flex flex-fill justify-content-end align-items-center">
                                <ActionsDropdown>
                                    <Dropdown.Item onClick={() => showAddDomainModal()} disabled={!hasAllPermissions(["domain:add-domain"])}>
                                        <AddDomainIcon className="text-success"/> {t("domains.add-domain")}
                                    </Dropdown.Item>
                                </ActionsDropdown>
                            </div>
                        </div>
                    </Row>
                </div>

                <PageContent className="flex-grow-1 min-h-0">
                    <Row className="h-100 w-100">
                        <Col className="h-100 overflow-auto">
                            <Table className="sticky sticky-x" bordered hover>
                                <thead className="sticky-top">
                                <tr>
                                    {selectedDomains.length === 0 ?
                                        <>
                                            <th className="th-w-min"><InvertSelection invertSelectionEvent={invertSelection}/></th>
                                            <th>{t("domains.domain")} <SortIcon sortDirection={sortDirection} setSortDirection={setSortDirection}/></th>
                                        </>
                                        :
                                        <th colSpan={2}>
                                            <Navbar className="bg-body-tertiary ps-2">
                                                {/* Add icon to first column which will clear the selection when clicked */}
                                                <ClearSelection clearSelectionEvent={() => setSelectedDomains([])} checked={true}/>

                                                <Navbar.Text className="ms-2">{selectedDomains.length} selected</Navbar.Text>

                                                <div className="vr ms-4 me-2"></div>

                                                <Nav>
                                                    <Nav.Link disabled={!hasAllPermissions(["domain:delete-domains"])} onClick={() => setShowDeleteConfirm(true)}>
                                                        <DeleteIcon className="text-danger"/> {t("domains.delete-selected")}
                                                    </Nav.Link>
                                                </Nav>
                                            </Navbar>
                                        </th>
                                    }
                                </tr>
                                </thead>
                                <tbody>
                                {
                                    domains.map((domainItem) => (
                                        <tr key={domainItem.domain} className="text-nowrap cursor-pointer">
                                            <td className="td-w-min">
                                                <div className="d-flex align-items-center">
                                                    <Form.Check
                                                        type="checkbox"
                                                        className="me-0"
                                                        checked={selectedDomains.includes(domainItem.domain)}
                                                        onChange={(e) => {onChangeSelect(e.target.checked, domainItem.domain)}}
                                                        disabled={domainItem.inUse}
                                                        inline
                                                    />

                                                    {domainItem.locality === UserLocality.Internal &&
                                                        <OverlayTrigger
                                                            placement={right}
                                                            overlay={
                                                                <Tooltip>
                                                                    {t("domains.internal-domain")}
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <InternalDomainIcon className="text-success table-icon-width table-icon-height ms-1"/>
                                                        </OverlayTrigger>
                                                    }
                                                </div>
                                            </td>
                                            <td onClick={() => navigate(getLinkTarget(domainItem.domain))}><Truncate value={domainItem.domain} maxLength={60}/></td>
                                        </tr>
                                    ))
                                }
                                </tbody>
                            </Table>
                        </Col>
                    </Row>
                </PageContent>

                <PageFooter
                    left={<RowsPerPage rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} setCurrentPage={setCurrentPage}/>}
                    right={<Pager itemCount={domainCount ?? 0}
                                  currentPage={currentPage}
                                  setCurrentPage={setCurrentPage}
                                  maxItemsPerPage={rowsPerPage}
                    />}
                />
            </div>
        </>
    );
}
